import { FormEvent, useEffect, useState } from 'react'
import { Button, Col, Form, Modal, Row } from 'react-bootstrap'
import { Pencil, Trash } from 'react-bootstrap-icons'

import { colors } from '../../../constants/salesStyles'
import getAllPartGroups from '../../../modules/redux/catalogs/getAllPartGroups'
import deleteAvitoProfile from '../../../modules/redux/sales/deleteAvitoProfile'
import getAvitoProfiles from '../../../modules/redux/sales/getAvitoProfiles'
import saveAvitoProfile from '../../../modules/redux/sales/saveAvitoProfile'
import warehousesSelectSearch from '../../../modules/redux/warehouseList/WarehousesSelectSearch'
import { useDispatch } from '../../../modules/store/customDispatch'
import { useSelector } from '../../../modules/store/customSelector'
import { avitoProfile } from '../../../types/sales/salesTypes'
import { profileFilenameFromName } from '../../../utils/avitoProfileFilename'
import CopyButton from '../../_atoms/CopyButton/CopyButton'
import Loader from '../../_atoms/Loader/Loader'
import OverlayWithText from '../../_atoms/OverlayWithText/OverlayWithText'

interface IProps {
    isOpen: boolean
    closeModal: () => void
}

const NAME_RE = /^[0-9A-Za-zА-Яа-яЁёІіЇїЄєҐґ_]{1,50}$/

type Draft = {
    name: string
    selectedCategories: string[]
    priceFrom: string
    priceTo: string
    warehouseIds: string[]
    priceMarkup: string
    phone: string
    managerName: string
    address: string
}

const emptyDraft = (): Draft => ({
    name: '',
    selectedCategories: [],
    priceFrom: '0',
    priceTo: '0',
    warehouseIds: [],
    priceMarkup: '1',
    phone: '',
    managerName: '',
    address: '',
})

const formatGeneratedAt = (timestamp: number | null) => {
    if (!timestamp) {
        return '—'
    }
    const date = new Date(timestamp)
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const ModalAvitoProfiles = ({ isOpen, closeModal }: IProps) => {
    const dispatch = useDispatch()
    const profiles = useSelector(state => state.sales.marketplaces.avitoProfiles)
    const categories = useSelector(state => state.partList.partGroups)
    const warehouses = useSelector(state => state.warehouseList.warehousesSS)
    const [isLoading, setIsLoading] = useState(false)
    const [formOpen, setFormOpen] = useState(false)
    const [editingId, setEditingId] = useState<string | undefined>(undefined)
    const [draft, setDraft] = useState<Draft>(emptyDraft())
    const [error, setError] = useState('')

    const loadData = async () => {
        setIsLoading(true)
        await dispatch(getAllPartGroups())
        await dispatch(warehousesSelectSearch(''))
        await dispatch(getAvitoProfiles())
        setIsLoading(false)
    }

    useEffect(() => {
        loadData()
    }, [])

    const toggleId = (list: string[], id: string, checked: boolean) =>
        checked ? [...list, id] : list.filter(item => item !== id)

    const openCreate = () => {
        setEditingId(undefined)
        setError('')
        setDraft({
            ...emptyDraft(),
            selectedCategories: categories.map(category => `${category.value}`),
            warehouseIds: warehouses.map(warehouse => `${warehouse.value}`),
        })
        setFormOpen(true)
    }

    const openEdit = (profile: avitoProfile) => {
        setEditingId(profile.id)
        setError('')
        setDraft({
            name: profile.name,
            selectedCategories: profile.selectedCategories || [],
            priceFrom: String(profile.priceFrom ?? 0),
            priceTo: String(profile.priceTo ?? 0),
            warehouseIds: profile.warehouseIds || [],
            priceMarkup: String(profile.priceMarkup ?? 1),
            phone: profile.phone || '',
            managerName: profile.managerName || '',
            address: profile.address || '',
        })
        setFormOpen(true)
    }

    const closeForm = () => {
        setFormOpen(false)
        setEditingId(undefined)
        setError('')
    }

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        const name = draft.name.trim()
        if (!NAME_RE.test(name)) {
            setError('Название: 1–50 символов, буквы, цифры и подчёркивание')
            return
        }
        if (!profileFilenameFromName(name)) {
            setError('Не удалось построить имя файла')
            return
        }
        const priceFrom = Number(draft.priceFrom || 0)
        const priceTo = Number(draft.priceTo || 0)
        const priceMarkup = Number(draft.priceMarkup)
        if (!Number.isFinite(priceFrom) || priceFrom < 0 || !Number.isFinite(priceTo) || priceTo < 0) {
            setError('Цена должна быть числом от 0')
            return
        }
        if (priceFrom > 0 && priceTo > 0 && priceTo < priceFrom) {
            setError('Цена до меньше цены от')
            return
        }
        if (!Number.isFinite(priceMarkup) || priceMarkup < 0) {
            setError('Коэффициент цены должен быть числом от 0')
            return
        }

        setIsLoading(true)
        setError('')
        try {
            await dispatch(
                saveAvitoProfile({
                    id: editingId,
                    name,
                    selectedCategories: draft.selectedCategories,
                    priceFrom,
                    priceTo,
                    warehouseIds: draft.warehouseIds,
                    priceMarkup,
                    phone: draft.phone.trim(),
                    managerName: draft.managerName.trim(),
                    address: draft.address.trim(),
                }),
            )
            closeForm()
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Не удалось сохранить профиль')
        }
        setIsLoading(false)
    }

    const handleDelete = async (profile: avitoProfile) => {
        if (!window.confirm(`Удалить профиль «${profile.name}»?`)) {
            return
        }
        setIsLoading(true)
        setError('')
        try {
            await dispatch(deleteAvitoProfile(profile.id))
            if (editingId === profile.id) {
                closeForm()
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Не удалось удалить профиль')
        }
        setIsLoading(false)
    }

    const filename = profileFilenameFromName(draft.name.trim())
    const parsedMarkup = Number(draft.priceMarkup)
    const hasMarkup = draft.priceMarkup !== '' && !Number.isNaN(parsedMarkup)
    const showMarkupInfo = hasMarkup && parsedMarkup !== 1
    const markupPercent = showMarkupInfo ? Math.round(Math.abs(1 - parsedMarkup) * 100) : 0
    const markupInfoText = showMarkupInfo
        ? parsedMarkup > 1
            ? `Применяется наценка ${markupPercent}%`
            : `Применяется скидка ${markupPercent}%`
        : ''

    return (
        <Modal show={isOpen} onHide={closeModal} centered size="lg" scrollable>
            {isLoading && (
                <OverlayWithText backgroundBootstrapColor={'bg-secondary'}>
                    <Loader diameterInPx={100} thicknessInPx={10} />
                </OverlayWithText>
            )}
            <Modal.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="mb-0">
                        <b>Настройки профилей авито</b>
                    </h4>
                    <Button type="button" onClick={openCreate}>
                        Создать профиль
                    </Button>
                </div>

                {formOpen && (
                    <Form onSubmit={handleSubmit} className="mb-4">
                        <Form.Group as={Row} className="mb-3" controlId="profileName">
                            <Form.Label column sm="4">
                                Название профиля
                            </Form.Label>
                            <Col sm="8">
                                <Form.Control
                                    value={draft.name}
                                    maxLength={50}
                                    onChange={e =>
                                        setDraft(prev => ({
                                            ...prev,
                                            name: e.target.value,
                                        }))
                                    }
                                />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3" controlId="profileFilename">
                            <Form.Label column sm="4">
                                Название файла
                            </Form.Label>
                            <Col sm="8">
                                <Form.Control value={filename} disabled />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm="4">
                                Категории
                            </Form.Label>
                            <Col sm="8" className="py-2">
                                {categories.map(category => (
                                    <Form.Check
                                        type="checkbox"
                                        id={`avito-profile-cat-${category.value}`}
                                        key={`${category.value}${category.name}`}
                                        label={category.name}
                                        checked={draft.selectedCategories.includes(`${category.value}`)}
                                        onChange={e =>
                                            setDraft(prev => ({
                                                ...prev,
                                                selectedCategories: toggleId(
                                                    prev.selectedCategories,
                                                    `${category.value}`,
                                                    e.target.checked,
                                                ),
                                            }))
                                        }
                                    />
                                ))}
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3" controlId="profilePriceFrom">
                            <Form.Label column sm="4">
                                Цена от
                            </Form.Label>
                            <Col sm="8">
                                <Form.Control
                                    type="number"
                                    min="0"
                                    value={draft.priceFrom}
                                    onChange={e =>
                                        setDraft(prev => ({
                                            ...prev,
                                            priceFrom: e.target.value,
                                        }))
                                    }
                                />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3" controlId="profilePriceTo">
                            <Form.Label column sm="4">
                                Цена до
                            </Form.Label>
                            <Col sm="8">
                                <Form.Control
                                    type="number"
                                    min="0"
                                    value={draft.priceTo}
                                    onChange={e =>
                                        setDraft(prev => ({
                                            ...prev,
                                            priceTo: e.target.value,
                                        }))
                                    }
                                />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm="4">
                                Склады
                            </Form.Label>
                            <Col sm="8" className="py-2">
                                {warehouses.length === 0 && <Form.Text className="text-muted">Складов нет</Form.Text>}
                                {warehouses.map(warehouse => (
                                    <Form.Check
                                        type="checkbox"
                                        id={`avito-profile-wh-${warehouse.value}`}
                                        key={`${warehouse.value}`}
                                        label={warehouse.name}
                                        checked={draft.warehouseIds.includes(`${warehouse.value}`)}
                                        onChange={e =>
                                            setDraft(prev => ({
                                                ...prev,
                                                warehouseIds: toggleId(
                                                    prev.warehouseIds,
                                                    `${warehouse.value}`,
                                                    e.target.checked,
                                                ),
                                            }))
                                        }
                                    />
                                ))}
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3" controlId="profilePriceMarkup">
                            <Form.Label column sm="4">
                                Коэффициент цены (priceMarkup)
                            </Form.Label>
                            <Col sm="8">
                                <Form.Control
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={draft.priceMarkup}
                                    onChange={e =>
                                        setDraft(prev => ({
                                            ...prev,
                                            priceMarkup: e.target.value,
                                        }))
                                    }
                                />
                                <Form.Text className="text-muted d-block">
                                    Итоговая цена = базовая цена × коэффициент
                                </Form.Text>
                                {markupInfoText && <Form.Text className="d-block">{markupInfoText}</Form.Text>}
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3" controlId="profilePhone">
                            <Form.Label column sm="4">
                                Телефон
                            </Form.Label>
                            <Col sm="8">
                                <Form.Control
                                    value={draft.phone}
                                    maxLength={32}
                                    onChange={e =>
                                        setDraft(prev => ({
                                            ...prev,
                                            phone: e.target.value,
                                        }))
                                    }
                                />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3" controlId="profileManager">
                            <Form.Label column sm="4">
                                Имя менеджера
                            </Form.Label>
                            <Col sm="8">
                                <Form.Control
                                    value={draft.managerName}
                                    maxLength={100}
                                    onChange={e =>
                                        setDraft(prev => ({
                                            ...prev,
                                            managerName: e.target.value,
                                        }))
                                    }
                                />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3" controlId="profileAddress">
                            <Form.Label column sm="4">
                                Адрес
                            </Form.Label>
                            <Col sm="8">
                                <Form.Control
                                    value={draft.address}
                                    maxLength={300}
                                    onChange={e =>
                                        setDraft(prev => ({
                                            ...prev,
                                            address: e.target.value,
                                        }))
                                    }
                                />
                            </Col>
                        </Form.Group>
                        {error && <div className="text-danger mb-3">{error}</div>}
                        <div className="d-flex justify-content-end">
                            <Button type="button" variant="outline-secondary" className="me-2" onClick={closeForm}>
                                Отмена
                            </Button>
                            <Button type="submit">Сохранить</Button>
                        </div>
                    </Form>
                )}

                {!formOpen && error && <div className="text-danger mb-3">{error}</div>}

                <div>
                    {(!profiles || profiles.length === 0) && <div className="text-muted">Профилей пока нет</div>}
                    {(profiles || []).map(profile => (
                        <div key={profile.id} className="d-flex align-items-center border-bottom py-2">
                            <div className="flex-grow-1">
                                <b>{profile.name}</b>
                                <CopyButton copyText={profile.link} overlayText="Ссылка на файл скопирована" />
                            </div>
                            <div className="text-nowrap me-3">{formatGeneratedAt(profile.generatedAt)}</div>
                            <div className="text-nowrap me-3" style={{ minWidth: 48, textAlign: 'right' }}>
                                {profile.adsCount === null || profile.adsCount === undefined
                                    ? '—'
                                    : profile.adsCount.toLocaleString('ru-RU')}
                            </div>
                            <div
                                className="me-2"
                                style={{ cursor: 'pointer' }}
                                title="Редактировать"
                                onClick={() => openEdit(profile)}
                            >
                                <Pencil color={colors.purple} />
                            </div>
                            <div style={{ cursor: 'pointer' }} title="Удалить" onClick={() => handleDelete(profile)}>
                                <Trash color={colors.purple} />
                            </div>
                        </div>
                    ))}
                </div>
            </Modal.Body>
        </Modal>
    )
}

export default ModalAvitoProfiles
